import { useState, useEffect } from "react";
import { AdminListingItem } from "../../../utils/api";

interface AiImageModalProps {
  listings: AdminListingItem[];
  onClose: () => void;
  onSuccess: (count: number) => void;
}

interface TaskStatus {
  listing_id: string;
  title: string;
  cover_image: string | null;
  intro_image: string | null;
  status: "pending" | "running" | "success" | "failed";
  error: string | null;
}

export default function AiImageModal({ listings, onClose, onSuccess }: AiImageModalProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [imageType, setImageType] = useState<"cover" | "intro" | "both">("both");
  const [isRunning, setIsRunning] = useState(false);
  const [tasks, setTasks] = useState<TaskStatus[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [batchId, setBatchId] = useState<string | null>(null);

  // 过滤列表
  const filtered = listings.filter((l) => {
    if (!searchTerm) return true;
    return l.title.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // 切换单选
  const toggleSelect = (id: string) => {
    if (isRunning) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  // 全选/取消全选
  const toggleAll = () => {
    if (isRunning) return;
    const filteredIds = filtered.map((l) => l.id);
    const allSelected = filteredIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...filteredIds])]);
    }
  };

  const allPageSelected =
    filtered.length > 0 && filtered.every((l) => selectedIds.includes(l.id));

  // 开始生成 (异步提交 + 轮询)
  const handleGenerate = async () => {
    if (selectedIds.length === 0) return;
    if (selectedIds.length > 50) {
      alert("单次最多选择50条信息");
      return;
    }

    setIsRunning(true);

    // 初始化任务状态
    const initialTasks: TaskStatus[] = selectedIds.map((id) => {
      const listing = listings.find((l) => l.id === id);
      return {
        listing_id: id,
        title: listing?.title || "未知",
        cover_image: null,
        intro_image: null,
        status: "running",
        error: null,
      };
    });
    setTasks(initialTasks);

    const token = localStorage.getItem("token");
    const siteId = localStorage.getItem("current_site_id") || "";

    try {
      // Step 1: 提交生成任务（秒级返回）
      const submitResp = await fetch("/api/admin/listings/ai-generate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Site-Id": siteId,
        },
        body: JSON.stringify({
          listing_ids: selectedIds,
          image_type: imageType,
        }),
      });

      const submitData = await submitResp.json();

      if (submitData.code !== 0) {
        setTasks((prev) =>
          prev.map((t) => ({ ...t, status: "failed", error: submitData.message || "提交失败" }))
        );
        setIsRunning(false);
        return;
      }

      const batchId = submitData.data.batch_id;
      if (!batchId) {
        setTasks((prev) =>
          prev.map((t) => ({ ...t, status: "failed", error: "未获取到批次ID" }))
        );
        setIsRunning(false);
        return;
      }

      // 保存 batchId 用于后续重试
      setBatchId(batchId);

      // 更新初始任务为从后端返回的实际任务
      const backendTasks = submitData.data.tasks || [];
      const groupedTasks: Record<string, TaskStatus> = {};
      for (const bt of backendTasks) {
        if (!groupedTasks[bt.listing_id]) {
          groupedTasks[bt.listing_id] = {
            listing_id: bt.listing_id,
            title: bt.title || "未知",
            cover_image: null,
            intro_image: null,
            status: "running",
            error: null,
          };
        }
        if (bt.status === "submit_failed") {
          groupedTasks[bt.listing_id].error = bt.error || "提交失败";
        }
      }
      setTasks((prev) => prev.map((t) => groupedTasks[t.listing_id] || t));

      // Step 2: 轮询查询结果（每3秒，最多200次=10分钟）
      let pollCount = 0;
      const maxPolls = 200;
      const pollInterval = 3000;
      let pollStopped = false;

      const poll = async () => {
        if (pollStopped) return;
        if (pollCount >= maxPolls) {
          // 超时：标记为 timeout 状态以便显示重试按钮
          setTasks((prev) =>
            prev.map((t) =>
              t.status === "running"
                ? { ...t, status: "failed" as const, error: "生成时间较长，点击下方「重新检查」按钮继续等待" }
                : t
            )
          );
          setIsRunning(false);
          return;
        }
        pollCount++;

        try {
          const statusResp = await fetch(
            `/api/admin/listings/ai-status?batch_id=${encodeURIComponent(batchId)}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "X-Site-Id": siteId,
              },
            }
          );
          const statusData = await statusResp.json();

          if (statusData.code === 0) {
            const results = statusData.data.results || [];
            setTasks((prev) =>
              prev.map((t) => {
                const r = results.find((r: any) => r.listing_id === t.listing_id);
                if (r) {
                  return {
                    ...t,
                    cover_image: r.cover_image || null,
                    intro_image: r.intro_image || null,
                    status: (r.status === "success" ? "success" : r.status === "failed" ? "failed" : "running") as TaskStatus["status"],
                    error: r.error || null,
                  };
                }
                return t;
              })
            );

            const newSuccessCount = results.filter((r: any) => r.status === "success").length;
            if (newSuccessCount > 0) {
              onSuccess(newSuccessCount);
            }

            if (statusData.data.all_done) {
              setIsRunning(false);
              return;
            }
          } else if (statusData.code === 404) {
            // 批次过期
            setTasks((prev) =>
              prev.map((t) =>
                t.status === "running"
                  ? { ...t, status: "failed" as const, error: "任务批次已过期，请重新提交" }
                  : t
              )
            );
            setIsRunning(false);
            return;
          }
        } catch {
          // 网络错误不中断轮询
        }

        setTimeout(poll, pollInterval);
      };

      // 开始轮询
      setTimeout(poll, pollInterval);

      // 保存 cleanup 函数引用，供 retry 使用
      (window as any).__aiPollStop = () => { pollStopped = true; };
    } catch (e: any) {
      setTasks((prev) =>
        prev.map((t) => ({ ...t, status: "failed", error: e.message || "网络错误" }))
      );
      setIsRunning(false);
    }
  };

  // 超时后重试：调用补拉接口强制检查
  const handleRetry = async () => {
    if (!batchId) return;

    setIsRunning(true);
    setTasks((prev) =>
      prev.map((t) =>
        t.status === "failed" && t.error?.includes("重新检查")
          ? { ...t, status: "running" as const, error: null }
          : t
      )
    );

    const token = localStorage.getItem("token");
    const siteId = localStorage.getItem("current_site_id") || "";

    try {
      const retryResp = await fetch(
        `/api/admin/listings/ai-retry?batch_id=${encodeURIComponent(batchId)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Site-Id": siteId,
          },
        }
      );
      const retryData = await retryResp.json();

      if (retryData.code === 0) {
        const results = retryData.data.results || [];
        setTasks((prev) =>
          prev.map((t) => {
            const r = results.find((r: any) => r.listing_id === t.listing_id);
            if (r) {
              return {
                ...t,
                cover_image: r.cover_image || null,
                intro_image: r.intro_image || null,
                status: (r.status === "success" ? "success" : r.status === "failed" ? "failed" : "running") as TaskStatus["status"],
                error: r.error || null,
              };
            }
            return t;
          })
        );

        const newSuccessCount = results.filter((r: any) => r.status === "success").length;
        if (newSuccessCount > 0) {
          onSuccess(newSuccessCount);
        }

        if (!retryData.data.all_done) {
          // 还有 pending 的，继续启动轮询
          let pollCount = 0;
          const maxPolls = 200;
          let pollStopped = false;
          (window as any).__aiPollStop = () => { pollStopped = true; };

          const pollAgain = async () => {
            if (pollStopped) return;
            if (pollCount >= maxPolls) {
              setTasks((prev) =>
                prev.map((t) =>
                  t.status === "running"
                    ? { ...t, status: "failed" as const, error: "生成时间较长，点击下方\"重新检查\"按钮继续等待" }
                    : t
                )
              );
              setIsRunning(false);
              return;
            }
            pollCount++;

            try {
              const statusResp = await fetch(
                `/api/admin/listings/ai-status?batch_id=${encodeURIComponent(batchId)}`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "X-Site-Id": siteId,
                  },
                }
              );
              const statusData = await statusResp.json();

              if (statusData.code === 0) {
                const results = statusData.data.results || [];
                setTasks((prev) =>
                  prev.map((t) => {
                    const r = results.find((r: any) => r.listing_id === t.listing_id);
                    if (r) {
                      return {
                        ...t,
                        cover_image: r.cover_image || null,
                        intro_image: r.intro_image || null,
                        status: (r.status === "success" ? "success" : r.status === "failed" ? "failed" : "running") as TaskStatus["status"],
                        error: r.error || null,
                      };
                    }
                    return t;
                  })
                );

                const count = results.filter((r: any) => r.status === "success").length;
                if (count > 0) onSuccess(count);

                if (statusData.data.all_done) {
                  setIsRunning(false);
                  return;
                }
              }
            } catch { /* ignore */ }
            setTimeout(pollAgain, 3000);
          };
          setTimeout(pollAgain, 3000);
        } else {
          setIsRunning(false);
        }
      } else if (retryData.code === 404) {
        setTasks((prev) =>
          prev.map((t) =>
            t.status === "running"
              ? { ...t, status: "failed" as const, error: "任务批次已过期，请重新提交" }
              : t
          )
        );
        setIsRunning(false);
      } else {
        setIsRunning(false);
      }
    } catch {
      setIsRunning(false);
    }
  };

  // 统计
  const runningCount = tasks.filter((t) => t.status === "running").length;
  const successCount = tasks.filter((t) => t.status === "success").length;
  const failedCount = tasks.filter((t) => t.status === "failed").length;
  const allDone = tasks.length > 0 && runningCount === 0;
  const hasTimeoutTasks = tasks.some((t) => t.status === "failed" && t.error?.includes("重新检查"));

  // 组件卸载时停止轮询
  useEffect(() => {
    return () => {
      if ((window as any).__aiPollStop) {
        (window as any).__aiPollStop();
      }
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-2xl shadow-xl flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <i className="ri-magic-line text-white text-sm" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 text-sm">AI 图片生成</h3>
              <p className="text-xs text-gray-400">使用 GPT Image 2 批量生成封面图和介绍图</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isRunning}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer disabled:opacity-40"
          >
            <i className="ri-close-line text-gray-500 text-sm" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {tasks.length > 0 ? (
            /* 进度展示 */
            <div className="p-5 space-y-4">
              {/* 进度条 */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">
                    {allDone ? "生成完成" : `正在生成... ${successCount + failedCount}/${tasks.length}`}
                  </span>
                  <span className="text-gray-400 text-xs">
                    成功 {successCount} / 失败 {failedCount}
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full transition-all duration-500 rounded-full"
                    style={{
                      width: `${((successCount + failedCount) / Math.max(tasks.length, 1)) * 100}%`,
                      background: allDone
                        ? failedCount === 0
                          ? "linear-gradient(90deg, #22c55e, #16a34a)"
                          : "linear-gradient(90deg, #f59e0b, #ef4444)"
                        : "linear-gradient(90deg, #8b5cf6, #ec4899)",
                    }}
                  />
                </div>
              </div>

              {/* 任务列表 */}
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {tasks.map((task) => (
                  <div
                    key={task.listing_id}
                    className={`p-3 rounded-xl border text-sm ${
                      task.status === "success"
                        ? "border-green-200 bg-green-50/30"
                        : task.status === "failed"
                        ? "border-red-200 bg-red-50/30"
                        : "border-purple-200 bg-purple-50/30"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-800 truncate font-medium">{task.title}</p>
                        {task.error && (
                          <p className="text-xs text-red-500 mt-0.5">{task.error}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {task.status === "running" && (
                          <span className="text-purple-500">
                            <i className="ri-loader-4-line animate-spin" />
                          </span>
                        )}
                        {task.status === "success" && (
                          <span className="text-green-500">
                            <i className="ri-checkbox-circle-line" />
                          </span>
                        )}
                        {task.status === "failed" && (
                          <span className="text-red-500">
                            <i className="ri-close-circle-line" />
                          </span>
                        )}
                      </div>
                    </div>

                    {/* 生成的图片预览 */}
                    {task.status === "success" && (task.cover_image || task.intro_image) && (
                      <div className="flex gap-2 mt-2">
                        {task.cover_image && (
                          <div className="relative group">
                            <img
                              src={task.cover_image}
                              alt="封面图"
                              className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                            />
                            <span className="absolute -top-1 -right-1 text-[10px] bg-purple-500 text-white px-1 rounded">
                              封面
                            </span>
                          </div>
                        )}
                        {task.intro_image && (
                          <div className="relative group">
                            <img
                              src={task.intro_image}
                              alt="介绍图"
                              className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                            />
                            <span className="absolute -top-1 -right-1 text-[10px] bg-pink-500 text-white px-1 rounded">
                              介绍
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* 选择列表 */
            <div className="p-5 space-y-4">
              {/* 搜索 */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-search-line text-gray-400 text-sm" />
                </div>
                <input
                  type="text"
                  placeholder="搜索信息标题..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400"
                />
              </div>

              {/* 图片类型选择 */}
              <div className="flex gap-2">
                {[
                  { key: "cover", label: "封面图", icon: "ri-image-line", desc: "从标题生成" },
                  { key: "intro", label: "介绍图", icon: "ri-file-text-line", desc: "从内容生成" },
                  { key: "both", label: "两者都生成", icon: "ri-stack-line", desc: "封面图+介绍图" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setImageType(opt.key as "cover" | "intro" | "both")}
                    className={`flex-1 p-3 rounded-xl border text-left cursor-pointer transition-all ${
                      imageType === opt.key
                        ? "border-purple-400 bg-purple-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <i className={`${opt.icon} ${imageType === opt.key ? "text-purple-500" : "text-gray-400"}`} />
                      <span className={`text-sm font-medium ${imageType === opt.key ? "text-purple-600" : "text-gray-600"}`}>
                        {opt.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400">{opt.desc}</p>
                  </button>
                ))}
              </div>

              {/* 信息列表 */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="flex items-center gap-2 cursor-pointer select-none" onClick={toggleAll}>
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={toggleAll}
                      className="w-3.5 h-3.5 accent-purple-500"
                    />
                    <span className="text-xs text-gray-500">
                      全选 ({selectedIds.length}/{filtered.length})
                    </span>
                  </label>
                  <span className="text-xs text-gray-400">单次最多50条</span>
                </div>
                <div className="max-h-64 overflow-y-auto space-y-1 border border-gray-100 rounded-xl p-1">
                  {filtered.length === 0 ? (
                    <p className="text-center text-sm text-gray-400 py-8">暂无匹配的信息</p>
                  ) : (
                    filtered.map((item) => (
                      <label
                        key={item.id}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          selectedIds.includes(item.id)
                            ? "bg-purple-50 border border-purple-200"
                            : "hover:bg-gray-50 border border-transparent"
                        } ${selectedIds.length >= 50 && !selectedIds.includes(item.id) ? "opacity-40 pointer-events-none" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(item.id)}
                          onChange={() => toggleSelect(item.id)}
                          className="w-3.5 h-3.5 accent-purple-500 shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-700 truncate">{item.title}</p>
                          <p className="text-xs text-gray-400 truncate">
                            {item.category_name || "未分类"} · {item.images?.length || 0}张图
                          </p>
                        </div>
                        {item.images && item.images.length > 0 && item.images[0] && !item.images[0].includes('example') ? (
                          <img
                            src={item.images[0]}
                            alt=""
                            className="w-10 h-10 object-cover rounded-md shrink-0"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                              (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <div className={`w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center shrink-0 ${item.images && item.images.length > 0 && !item.images[0].includes('example') ? 'hidden' : ''}`}>
                          <i className="ri-image-line text-gray-300 text-sm" />
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between shrink-0">
          {tasks.length > 0 ? (
            <>
              <p className="text-xs text-gray-400">
                {hasTimeoutTasks
                  ? "部分任务生成时间较长，点击\"重新检查\"继续等待"
                  : allDone
                  ? "生成完成后图片将自动添加到信息中"
                  : "生成中，请耐心等待..."}
              </p>
              <div className="flex gap-2">
                {hasTimeoutTasks && !isRunning && (
                  <button
                    onClick={handleRetry}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm bg-purple-500 text-white rounded-xl hover:bg-purple-600 cursor-pointer transition-colors"
                  >
                    <i className="ri-refresh-line" />
                    重新检查
                  </button>
                )}
                <button
                  onClick={() => {
                    if ((window as any).__aiPollStop) (window as any).__aiPollStop();
                    onClose();
                  }}
                  className={`px-4 py-2 text-sm rounded-xl cursor-pointer transition-colors ${
                    allDone && !hasTimeoutTasks
                      ? "bg-purple-500 text-white hover:bg-purple-600"
                      : "border border-gray-200 text-gray-500"
                  }`}
                >
                  {allDone && !hasTimeoutTasks ? "完成" : "关闭"}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-gray-400">
                选择信息后点击开始，AI 将自动生成对应图片
              </p>
              <div className="flex gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer"
                >
                  取消
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={selectedIds.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <i className="ri-magic-line" />
                  开始生成 ({selectedIds.length})
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
